<template>
  <section id="photos" class="photos-section">
    <div class="photos-container">
      <div class="section-badge">
        <span>{{ t.photos.badge }}</span>
      </div>

      <h2 class="section-title">{{ t.photos.title }}</h2>
      <span class="section-subtitle-gradient">{{ t.photos.tagline }}</span>

      <p class="section-subtitle">
        {{ t.photos.description }}
      </p>

      <!-- Interactive Photos Switcher -->
      <div class="photos-showcase">
        <div class="showcase-nav">
          <button
            v-for="(view, index) in localizedPhotoViews"
            :key="index"
            @click="activeViewIndex = index"
            class="view-tab"
            :class="{ active: activeViewIndex === index }"
          >
            <span class="view-num">0{{ index + 1 }}</span>
            <span class="view-name">{{ view.name }}</span>
          </button>
        </div>

        <div class="showcase-display">
          <div class="display-image-box">
            <img
              :src="currentView.image"
              :alt="currentView.name"
              class="display-image"
            />
          </div>

          <div class="display-details">
            <span class="feature-tag">{{ currentView.badge }}</span>
            <h3 class="feature-title">{{ currentView.heading }}</h3>
            <p class="feature-desc">{{ currentView.description }}</p>

            <ul class="feature-bullets">
              <li v-for="(bullet, bIdx) in currentView.bullets" :key="bIdx">
                <span class="check-icon">✓</span>
                <span>{{ bullet }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { useI18n } from '@/i18n';

export default {
  name: 'PhotosSection',
  setup() {
    const { t } = useI18n();
    return { t };
  },
  data() {
    return {
      activeViewIndex: 0,
      photoImages: [
        'https://www.apple.com/newsroom/images/2024/06/ios-18-makes-iphone-more-personal-capable-and-intelligent-than-ever/article/Apple-WWDC24-iOS-18-photos-single-view-240610_inline.jpg.large.jpg',
        'https://www.apple.com/newsroom/images/2024/06/ios-18-makes-iphone-more-personal-capable-and-intelligent-than-ever/article/Apple-WWDC24-iOS-18-photos-collections-240610_inline.jpg.large.jpg',
        'https://www.apple.com/newsroom/images/2024/06/ios-18-makes-iphone-more-personal-capable-and-intelligent-than-ever/article/Apple-WWDC24-iOS-18-Apple-Intelligence-Clean-Up-240610_inline.jpg.large.jpg'
      ]
    };
  },
  computed: {
    localizedPhotoViews() {
      return [
        {
          name: this.t.photos.tab1Name,
          badge: this.t.photos.tab1Name,
          heading: this.t.photos.tab1Heading,
          description: this.t.photos.tab1Desc,
          bullets: [
            this.t.photos.tab1Bullet1,
            this.t.photos.tab1Bullet2,
            this.t.photos.tab1Bullet3
          ],
          image: this.photoImages[0]
        },
        {
          name: this.t.photos.tab2Name,
          badge: this.t.photos.tab2Name,
          heading: this.t.photos.tab2Heading,
          description: this.t.photos.tab2Desc,
          bullets: [
            this.t.photos.tab2Bullet1,
            this.t.photos.tab2Bullet2,
            this.t.photos.tab2Bullet3
          ],
          image: this.photoImages[1]
        },
        {
          name: this.t.photos.tab3Name,
          badge: this.t.photos.tab3Name,
          heading: this.t.photos.tab3Heading,
          description: this.t.photos.tab3Desc,
          bullets: [
            this.t.photos.tab3Bullet1,
            this.t.photos.tab3Bullet2,
            this.t.photos.tab3Bullet3
          ],
          image: this.photoImages[2]
        }
      ];
    },
    currentView() {
      return this.localizedPhotoViews[this.activeViewIndex] || this.localizedPhotoViews[0];
    }
  }
}
</script>

<style scoped>
.photos-section {
  padding: 100px 24px;
  background: #ffffff;
  color: #1d1d1f;
  text-align: center;
}

.photos-container {
  max-width: 1120px;
  margin: 0 auto;
}

.section-badge {
  display: inline-flex;
  align-items: center;
  background: rgba(0, 0, 0, 0.06);
  color: #1d1d1f;
  font-size: 13px;
  font-weight: 500;
  padding: 6px 14px;
  border-radius: 980px;
  margin-bottom: 20px;
}

.section-title {
  font-size: clamp(34px, 5.5vw, 56px);
  font-weight: 600;
  letter-spacing: -0.005em;
  line-height: 1.07143;
  color: #1d1d1f;
  margin-bottom: 4px;
}

.section-subtitle-gradient {
  font-size: clamp(30px, 5vw, 52px);
  line-height: 1.1;
  font-weight: 600;
  letter-spacing: 0.004em;
  background: linear-gradient(to right,
      #ff3b30,
      #ff9500,
      #b59722,
      #4c63d9,
      #187099,
      #007aff,
      #36349e,
      #3211b4);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  display: inline-block;
  margin-bottom: 20px;
}

.section-subtitle {
  max-width: 760px;
  margin: 0 auto 50px;
  font-size: 17px;
  line-height: 1.45;
  color: #86868b;
  font-weight: 400;
}

/* Photos Showcase */
.photos-showcase {
  background: #f5f5f7;
  border-radius: 28px;
  padding: 40px;
}

.showcase-nav {
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-bottom: 40px;
  flex-wrap: wrap;
}

.view-tab {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 22px;
  border-radius: 980px;
  border: 1px solid #d2d2d7;
  background: #ffffff;
  color: #6e6e73;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.view-tab:hover {
  border-color: #0071e3;
  color: #0071e3;
}

.view-tab.active {
  background: #0071e3;
  color: #ffffff;
  border-color: #0071e3;
}

.view-num {
  font-size: 12px;
  opacity: 0.8;
}

.showcase-display {
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 40px;
  align-items: center;
  text-align: left;
}

.display-image-box {
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 16px 36px rgba(0, 0, 0, 0.08);
}

.display-image {
  width: 100%;
  height: auto;
  display: block;
}

.display-details {
  padding: 10px;
}

.feature-tag {
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
  color: #0071e3;
  display: block;
  margin-bottom: 8px;
}

.feature-title {
  font-size: 30px;
  font-weight: 600;
  color: #1d1d1f;
  line-height: 1.2;
  margin-bottom: 14px;
}

.feature-desc {
  font-size: 16px;
  line-height: 1.5;
  color: #6e6e73;
  margin-bottom: 24px;
}

.feature-bullets {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.feature-bullets li {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 15px;
  color: #1d1d1f;
  font-weight: 500;
}

.check-icon {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #34c759;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
}

@media (max-width: 860px) {
  .showcase-display {
    grid-template-columns: 1fr;
  }
}
</style>
